import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileUp, ListChecks, Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { dirOf } from '@/lib/i18n';
import { useTrade } from '@/lib/trade';
import { formatDuration, formatLead, JOB_TYPES_KEY, jobTypesApi, type JobType } from './job-types-api';
import { ImportJobTypesDialog } from './import-job-types-dialog';

/**
 * סוגי העבודה של העסק: מה מופיע ב"משימה חדשה", איזו רשימת בדיקה נפתחת,
 * ואיזה כישור נדרש לשיבוץ. נקודת פתיחה — סוגי העבודה המומלצים לתחום.
 */
export function JobTypesPanel() {
  const { t } = useTranslation(['job-types', 'common']);
  const trade = useTrade();
  const toast = useToast();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: JOB_TYPES_KEY, queryFn: ({ signal }) => jobTypesApi.list(signal) });
  const [editing, setEditing] = useState<JobType | 'new' | null>(null);
  const [importing, setImporting] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: JOB_TYPES_KEY });
  const addDefaults = useAddTradeDefaults();
  const remove = useMutation({
    mutationFn: (id: string) => jobTypesApi.remove(id),
    onSuccess: async () => {
      toast.success(t('panel.removed'), t('panel.removedHint'));
      await refresh();
    },
    onError: (e) => toast.error(t('common:errors.removeFailed'), e instanceof ApiError ? e.message : undefined),
  });

  if (list.isPending) return <Skeleton className="h-72" />;
  if (list.isError) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle>{t('panel.title')}</CardTitle>
          <CardDescription>
            {t('panel.description', { worker: trade.worker.one })}
          </CardDescription>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" loading={addDefaults.isPending} onClick={() => addDefaults.mutate()}>
            <Sparkles aria-hidden />
            {t('panel.recommended', { trade: trade.label })}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setImporting(true)} data-track="job-type.import">
            <FileUp aria-hidden />
            {t('panel.importFromFile')}
          </Button>
          <Button size="sm" onClick={() => setEditing('new')}>
            <Plus aria-hidden />
            {t('panel.new')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {list.data.length === 0 ? (
          <EmptyState
            icon={ListChecks}
            title={t('panel.emptyTitle')}
            description={t('panel.emptyDescription', { trade: trade.label })}
          />
        ) : (
          <ul className="divide-y divide-border">
            {list.data.map((jt) => (
              <li key={jt.id} className="flex items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-fg">{jt.name}</p>
                  <p className="truncate text-xs text-fg-muted">
                    {formatDuration(jt.durationMinutes)} · {formatLead(jt.leadTimeDays)}
                    {' · '}
                    {jt.defaultChecklist.length
                      ? jt.defaultChecklist.map((c) => c.label).join(' · ')
                      : t('panel.noChecklist')}
                  </p>
                </div>
                {jt.requiredSkill ? <Badge tone="neutral">{jt.requiredSkill}</Badge> : null}
                <Button variant="ghost" size="sm" aria-label={t('panel.editAria', { name: jt.name })} onClick={() => setEditing(jt)}>
                  <Pencil aria-hidden />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('panel.removeAria', { name: jt.name })}
                  loading={remove.isPending && remove.variables === jt.id}
                  onClick={() => {
                    if (window.confirm(t('panel.confirmRemove', { name: jt.name }))) remove.mutate(jt.id);
                  }}
                >
                  <Trash2 aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      {editing ? (
        <JobTypeDialog jobType={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      ) : null}
      {importing ? <ImportJobTypesDialog onClose={() => setImporting(false)} /> : null}
    </Card>
  );
}

/** משותף לפאנל ולמצב הריק בדיאלוג "משימה חדשה". */
export function useAddTradeDefaults() {
  const { t } = useTranslation(['job-types', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => jobTypesApi.addTradeDefaults(),
    onSuccess: async ({ added }) => {
      if (added) toast.success(t('defaults.added', { count: added }), t('defaults.addedHint'));
      else toast.info(t('defaults.allExist'));
      await qc.invalidateQueries({ queryKey: JOB_TYPES_KEY });
    },
    onError: (e) => toast.error(t('defaults.addFailed'), e instanceof ApiError ? e.message : undefined),
  });
}

const DURATIONS = [30, 45, 60, 90, 120, 180, 240, 300, 360, 480, 600];

function JobTypeDialog({ jobType, onClose }: { jobType: JobType | null; onClose: () => void }) {
  const { t } = useTranslation(['job-types', 'common']);
  const trade = useTrade();
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState(jobType?.name ?? '');
  const [skill, setSkill] = useState(jobType?.requiredSkill ?? '');
  const [checklist, setChecklist] = useState((jobType?.defaultChecklist ?? []).map((c) => c.label).join('\n'));
  const [duration, setDuration] = useState(jobType?.durationMinutes ?? 120);
  const [lead, setLead] = useState(String(jobType?.leadTimeDays ?? 0));
  const leadDays = Math.min(365, Math.max(0, Math.round(Number(lead) || 0)));

  const save = useMutation({
    mutationFn: () => {
      // שורה שלא השתנתה שומרת את קוד המחירון / המק"ט שהיו לה.
      const previous = new Map((jobType?.defaultChecklist ?? []).map((c) => [c.label, c]));
      const items = checklist
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .slice(0, 50)
        .map((label) => previous.get(label) ?? { label });
      const input = {
        name: name.trim(),
        requiredSkill: skill.trim() || null,
        defaultChecklist: items,
        durationMinutes: duration,
        leadTimeDays: leadDays,
      };
      return jobType ? jobTypesApi.update(jobType.id, input) : jobTypesApi.create(input);
    },
    onSuccess: async () => {
      toast.success(jobType ? t('dialog.updated') : t('dialog.added'));
      await qc.invalidateQueries({ queryKey: JOB_TYPES_KEY });
      onClose();
    },
    onError: (e) =>
      toast.error(
        t('common:errors.saveFailed'),
        e instanceof ApiError && e.status === 409 ? t('dialog.duplicate') : e instanceof ApiError ? e.message : undefined,
      ),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent aria-describedby="job-type-desc">
        <DialogHeader>
          <DialogTitle>{jobType ? t('dialog.editTitle', { name: jobType.name }) : t('dialog.newTitle')}</DialogTitle>
          <DialogDescription id="job-type-desc">{t('dialog.description')}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label={t('dialog.name')} htmlFor="jt-name" required>
            <Input id="jt-name" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('dialog.namePlaceholder')} />
          </Field>
          <Field
            label={t('dialog.skill')}
            htmlFor="jt-skill"
            hint={t('dialog.skillHint', { worker: trade.worker.one })}
          >
            <Input id="jt-skill" maxLength={64} value={skill} onChange={(e) => setSkill(e.target.value)} placeholder={t('dialog.skillPlaceholder')} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('dialog.duration')} htmlFor="jt-duration" hint={t('dialog.durationHint')}>
              <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
                <SelectTrigger id="jt-duration" aria-label={t('dialog.duration')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...new Set([...DURATIONS, duration])]
                    .sort((a, b) => a - b)
                    .map((m) => (
                      <SelectItem key={m} value={String(m)}>
                        {formatDuration(m)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={t('dialog.lead')} htmlFor="jt-lead" hint={t('dialog.leadHint')}>
              <Input id="jt-lead" type="number" inputMode="numeric" min={0} max={365} dir="ltr" value={lead} onChange={(e) => setLead(e.target.value)} />
            </Field>
          </div>
          <Field label={t('dialog.checklist')} htmlFor="jt-checklist" hint={t('dialog.checklistHint', { worker: trade.worker.one })}>
            <Textarea id="jt-checklist" dir={dirOf()} rows={6} value={checklist} onChange={(e) => setChecklist(e.target.value)} />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={!name.trim()} loading={save.isPending} onClick={() => save.mutate()}>
            {t('common:actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
